import app from 'flarum/forum/app';
import AdvertisePage from './components/AdvertisePage';
import BusinessPage from './components/BusinessPage';
import AdvertiserPage from './components/AdvertiserPage';
import ModeratorPage from './components/ModeratorPage';
import SponsorSlot, { renderAd, watchImpression, fillSlot, slotUrl, noteShown } from './components/SponsorSlot';
import { extend } from 'flarum/common/extend';
import Switch from 'flarum/common/components/Switch';
import Discussion from 'flarum/common/models/Discussion';
import Badge from 'flarum/common/components/Badge';

// Discussions pinned as sponsored sticky posts. Loaded once, the first time a list or badge
// needs it: app.forum (and so the API URL) doesn't exist yet while initializers run in Flarum 2.
let sponsoredIds = new Set();
let sponsoredRequested = false;
function loadSponsoredIds() {
  if (sponsoredRequested || !app.forum) return;
  sponsoredRequested = true;
  app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/voucha-ads/sponsored-discussions',
    errorHandler: () => {}
  }).then(r => {
    sponsoredIds = new Set((r.ids || []).map(String));
    if (sponsoredIds.size) m.redraw();
  }).catch(() => {});
}
app.initializers.add('voucha-ads', () => {
  // For other extensions (e.g. the calculators): m(app.vouchaAds.Slot, { placement, tool, country })
  // Slot: a component for Mithril views (calculators). fillSlot(el, opts): for DOM built outside Mithril (theme sidebar).
  app.vouchaAds = {
    Slot: SponsorSlot,
    fillSlot
  };

  // "Sponsored" badge on sponsored sticky posts (same mechanism as Flarum's Sticky badge)
  extend(Discussion.prototype, 'badges', function (badges) {
    loadSponsoredIds();
    if (sponsoredIds.has(String(this.id()))) {
      badges.add('vouchaSponsored', m(Badge, {
        type: "sponsored",
        label: "Sponsored",
        icon: "fas fa-bullhorn",
        tabindex: "0"
      }), 11);
    }
  });

  // Sponsored cards in the discussion list, on the first page: after the 3rd discussion, and a different ad
  // after the 8th (none there if no other ad qualifies).
  // Like flarum-ext-ad-management, the ad is fetched once into a cache and drawn as plain markup inside
  // Flarum's list (no self-fetching component there). The entry is only added once there's an ad, and
  // it's its own keyed <li> like the discussions around it, so Mithril's list diffing stays intact.
  extend('flarum/forum/components/DiscussionList', 'view', function (vdom) {
    loadSponsoredIds();
    const state = this.attrs.state;
    if (!state || state.isSearchResults?.()) return;
    const entry = feedAd(currentTag());
    const ad = entry && entry.ad;
    if (!ad) return;
    const ul = findFeed(vdom);
    if (!ul || !Array.isArray(ul.children)) return;

    // Mithril turns each loaded page's array of <li> into a fragment vnode (tag '['); accept either shape
    const page = ul.children[0];
    const first = Array.isArray(page) ? page : page && page.tag === '[' && Array.isArray(page.children) ? page.children : null;
    if (!first || first.length < 3 || first.some(li => li && li.key === 'voucha-feed-ad')) return;
    const card = (key, a) => m("li", {
      key: key,
      className: "DiscussionList-sponsored",
      role: "complementary",
      oncreate: v => watchImpression(v.dom, a),
      onupdate: v => watchImpression(v.dom, a)
    }, renderAd(a, {
      compact: true
    }));
    // Positions count only the discussions the reader sees: the theme hides some rows (extra polls on the
    // home feed, a tag's highlighted posts), which would otherwise pull the cards up the list
    const hidden = app.vouchaFeedHidden || (() => false);
    const after = n => {
      let seen = 0;
      for (let i = 0; i < first.length; i++) {
        const li = first[i];
        const item = li && li.children && li.children[0];
        const d = item && item.attrs && item.attrs.discussion;
        if (d && hidden(d)) continue;
        seen++;
        if (seen === n) return i + 1;
      }
      return -1;
    };
    const at3 = after(3);
    const at8 = after(8);
    if (at3 === -1) return;
    // the lower card first, so the upper one doesn't shift its place
    if (entry.ad2 && at8 !== -1) first.splice(at8, 0, card('voucha-feed-ad-2', entry.ad2));
    first.splice(at3, 0, card('voucha-feed-ad', ad));
  });
  app.routes['voucha-ads.advertise'] = {
    path: '/advertise',
    component: AdvertisePage
  };
  app.routes['voucha-ads.advertiser'] = {
    path: '/advertiser',
    component: AdvertiserPage
  };
  app.routes['voucha-ads.business'] = {
    path: '/business',
    component: BusinessPage
  };
  app.routes['voucha-ads.moderators'] = {
    path: '/moderators',
    component: ModeratorPage
  };
});
function findFeed(node) {
  if (!node || typeof node !== 'object') return null;
  if (Array.isArray(node)) {
    for (const child of node) {
      const hit = findFeed(child);
      if (hit) return hit;
    }
    return null;
  }
  const cls = node.attrs && (node.attrs.className || node.attrs.class) || '';
  if (node.tag === 'ul' && String(cls).includes('DiscussionList-discussions')) return node;
  return findFeed(node.children);
}

// ---------- Feed ad: a fresh pick on every page view ----------
let feedEntry = null;
function currentTag() {
  try {
    return m.route.param('tags') || '';
  } catch (e) {
    return '';
  }
}
function feedAd(tag) {
  if (!app.forum) return null;
  let route = '';
  try {
    route = m.route.get() || '';
  } catch (e) {}

  // New page view (any route change, including within a tag) = new pick, so ads rotate and never go stale
  if (!feedEntry || feedEntry.route !== route) {
    const entry = {
      route,
      ad: null,
      ad2: null
    };
    feedEntry = entry;
    app.request({
      method: 'GET',
      url: slotUrl({
        placement: 'forum_feed',
        tag
      }),
      errorHandler: () => {}
    }).then(r => {
      if (feedEntry !== entry) return;
      entry.ad = r.ad || null;
      noteShown('forum_feed', entry.ad, r.cap);
      if (!entry.ad) return;
      m.redraw();
      // the second card: any other ad, never a repeat of the first (strict)
      return app.request({
        method: 'GET',
        url: slotUrl({
          placement: 'forum_feed',
          tag,
          avoid: String(entry.ad.id),
          strict: '1'
        }),
        errorHandler: () => {}
      }).then(r2 => {
        if (feedEntry !== entry || !r2 || !r2.ad || r2.ad.id === entry.ad.id) return;
        entry.ad2 = r2.ad;
        noteShown('forum_feed', entry.ad2, r2.cap); // counts toward the daily cap like the first
        m.redraw();
      });
    }).catch(() => {});
  }
  return feedEntry;
}

// Settings > Privacy: personalised ads (where members live / are from), on unless switched off
app.initializers.add('voucha-ads-personalised-setting', () => {
  extend('flarum/forum/components/SettingsPage', 'privacyItems', function (items) {
    const user = this.user;
    if (!user) return;
    const prefs = user.preferences() || {};
    const on = prefs.vouchaPersonalisedAds !== false;
    items.add('vouchaPersonalisedAds', m(Switch, {
      state: on,
      onchange: value => {
        this.vouchaAdsSaving = true;
        user.savePreferences({
          vouchaPersonalisedAds: value
        }).then(() => {
          this.vouchaAdsSaving = false;
          m.redraw();
        });
      },
      loading: this.vouchaAdsSaving
    }, "Show ads based on where I live and where I\u2019m from"), -10);
  });
});